/**
 * Calendar days (`YYYY-MM-DD`, as the API sends a gift's or a batch's day)
 * in words a person reads at a glance: "Sun, Oct 18", "Today". Read at noon
 * UTC, so the day never slips to its neighbour in any time zone.
 */

const at = (day: string) => new Date(`${day}T12:00:00Z`)

/** "Sun, Oct 18" (this year), "Sun, Oct 18, 2025" (another year). */
export function formatDay(
  day: string,
  today: string = new Date().toISOString().slice(0, 10),
): string {
  const date = at(day)
  if (Number.isNaN(date.getTime())) return day
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(day.slice(0, 4) === today.slice(0, 4) ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  })
}

/** A day as a list's heading: "Today", "Yesterday", else as `formatDay`. */
export function dayHeading(
  day: string,
  today: string = new Date().toISOString().slice(0, 10),
): string {
  if (day === today) return 'Today'
  const yesterday = new Date(at(today).getTime() - 86_400_000).toISOString().slice(0, 10)
  if (day === yesterday) return 'Yesterday'
  return formatDay(day, today)
}

/** "Jan 1 – Oct 19" (a range in one year), with years when they differ. */
export function formatDayRange(from: string, to: string): string {
  const sameYear = from.slice(0, 4) === to.slice(0, 4)
  const part = (day: string, year: boolean) =>
    at(day).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      ...(year ? { year: 'numeric' } : {}),
      timeZone: 'UTC',
    })
  return `${part(from, !sameYear)} – ${part(to, !sameYear)}`
}

/** Days in order, each with its rows: a list grouped under day headings. */
export function groupByDay<T>(
  rows: readonly T[],
  dayOf: (row: T) => string,
): { day: string; rows: T[] }[] {
  const groups: { day: string; rows: T[] }[] = []
  for (const row of rows) {
    const day = dayOf(row)
    const last = groups.at(-1)
    if (last && last.day === day) last.rows.push(row)
    else groups.push({ day, rows: [row] })
  }
  return groups
}
